import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Navbar.css';
import { auth } from './firebase';
import { signOut } from 'firebase/auth';
import { User, LogOut, ChevronUp, ChevronDown } from 'lucide-react';

const Navbar = ({ user }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [satDropdownOpen, setSatDropdownOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const hamburgerMenuRef = useRef(null);
  const navbarRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const dropdownRef = useRef(null);
  const satDropdownRef = useRef(null);

  const handleMenuClick = () => {
    setMenuOpen(!menuOpen);
    setProfileDropdownOpen(false);
    setDropdownOpen(false);
    setSatDropdownOpen(false);
  };

  const handleDropdownClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropdownOpen((prev) => !prev);
    setSatDropdownOpen(false);
    setProfileDropdownOpen(false);
  };

  const handleSatDropdownClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSatDropdownOpen((prev) => !prev);
    setDropdownOpen(false);
    setProfileDropdownOpen(false);
  };

  const handleLinkClick = () => {
    setMenuOpen(false);
    setDropdownOpen(false);
    setSatDropdownOpen(false);
    setProfileDropdownOpen(false);
  };

  const handleProfileClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setProfileDropdownOpen((prev) => !prev);
    setMenuOpen(false);
    setDropdownOpen(false);
    setSatDropdownOpen(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        satDropdownRef.current &&
        !satDropdownRef.current.contains(event.target) &&
        !hamburgerMenuRef.current.contains(event.target)
      ) {
        setProfileDropdownOpen(false);
        setDropdownOpen(false);
        setSatDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileDropdownRef, dropdownRef, satDropdownRef]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setMenuOpen(false);
        setDropdownOpen(false);
        setSatDropdownOpen(false);
        setProfileDropdownOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <nav className="navbar" ref={navbarRef}>
      <div className="navbar-logo-container">
        <Link to="/" onClick={handleLinkClick}>
          <img src="./logo.jpg" alt="Campus Goals Logo" className="navbar-logo" />
        </Link>
      </div>

      <ul className={`navbar-menu ${menuOpen ? 'active' : ''}`}>
        <li><Link to="/AboutUs" onClick={handleLinkClick}>About Us</Link></li>
        <li><Link to="/disclaimer" onClick={handleLinkClick}>Disclaimer</Link></li>
        <li><Link to="/ContactForm" onClick={handleLinkClick}>Contact Us</Link></li>
        <li><Link to="/blog" onClick={handleLinkClick}>Blog</Link></li>
        <li className={`dropdown ${satDropdownOpen ? 'active' : ''}`} ref={satDropdownRef}>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="#" onClick={handleSatDropdownClick}>SAT Prep</a>
          <ul className={`dropdown-menu ${satDropdownOpen ? 'active' : ''}`}>
            <li><Link to="/satmathQuiz" onClick={handleLinkClick}>Math Module</Link></li>
            <li><Link to="/satrwquiz" onClick={handleLinkClick}>Reading and Writing Module</Link></li>
            <li><Link to="/quiz" onClick={handleLinkClick}>Vocabulary Quiz</Link></li>
            <li><Link to="/synonymsQuiz" onClick={handleLinkClick}>Synonyms/Antonyms Quiz</Link></li>
            <li><Link to="/vocabularyBuilder" onClick={handleLinkClick}>Vocabulary Builder</Link></li>
          </ul>
        </li>
        <li className={`dropdown ${dropdownOpen ? 'active' : ''}`} ref={dropdownRef}>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="#" onClick={handleDropdownClick}>GRE Prep</a>
          <ul className={`dropdown-menu ${dropdownOpen ? 'active' : ''}`}>
            <li><Link to="/quiz" onClick={handleLinkClick}>Vocabulary Quiz</Link></li>
            <li><Link to="/tcseQuiz" onClick={handleLinkClick}>Text Completion Quiz</Link></li>
            <li><Link to="/rcQuiz" onClick={handleLinkClick}>Reading Comprehension Quiz</Link></li>
            <li><Link to="/synonymsQuiz" onClick={handleLinkClick}>Synonyms/Antonyms Quiz</Link></li>
            <li><Link to="/vocabularyBuilder" onClick={handleLinkClick}>Vocabulary Builder</Link></li>
            <li><Link to="/mathQuiz" onClick={handleLinkClick}>Math Quiz</Link></li>
          </ul>
        </li>
      </ul>

      {user && (
      <div className="profile-dropdown-container">
        <div
          className={`profile-dropdown ${profileDropdownOpen ? 'active' : ''}`}
          onClick={handleProfileClick}
          ref={profileDropdownRef}
        >
          <img src={user.photoURL} alt="Profile" className="profile-picture" />
          {profileDropdownOpen ? (
            <ChevronUp className="profile-dropdown-arrow" size={18} />
          ) : (
            <ChevronDown className="profile-dropdown-arrow" size={18} />
          )}
          <ul className={`profile-dropdown-menu ${profileDropdownOpen ? 'active' : ''}`}>
            <li>
              <Link to="/ProfilePage" onClick={handleLinkClick}>
                <User size={18} />
                Profile
              </Link>
            </li>
            <li>
              <button onClick={handleSignOut}>
                <LogOut size={18} />
                Sign Out
              </button>
            </li>
          </ul>
        </div>
      </div>
      )}

      <button
        className={`hamburger-menu ${menuOpen ? 'open' : ''}`}
        onClick={handleMenuClick}
        ref={hamburgerMenuRef}
      >
        <span></span>
        <span></span>
        <span></span>
      </button>
    </nav>
  );
};

export default Navbar;